import { useEffect } from 'react';
// react-helmet-async's peer deps only cover React 16-18 and it silently
// fails to commit updates to document.head under React 19 (this app is on
// 19.2.3) — every page ended up showing whichever page happened to render
// first. React 19 has native support for hoisting <title>/<meta>/<link>
// rendered anywhere in the tree, so these are just rendered directly
// instead of wrapped in a Helmet component.
import seoService, { SEOMetadata, type ProductSEO as ProductSEOFields } from '@/services/seo.service';
import analyticsService from '@/services/analytics.service';
import { useSiteBrand } from '@/context/SiteConfigContext';

const SITE_URL = 'https://marplecompany.co.za';

interface SEOProps {
  metadata?: SEOMetadata;
  page?: string;
  params?: Record<string, any>;
  product?: ProductSEOFields['product'];
  breadcrumbs?: Array<{ name: string; url: string }>;
  faqs?: Array<{ question: string; answer: string }>;
  // Per-page direct overrides — this is what every real page in the app
  // actually uses (Home/Shop/ProductDetail/Blog/Article/CheckoutPage/
  // SkinAnalysisWizard/About). Takes priority over `metadata`/`page` when
  // provided, rather than being silently ignored.
  title?: string;
  description?: string;
  path?: string;
  schema?: Record<string, any> | Record<string, any>[];
  /** Keep this page out of search results (e.g. the 404 page). */
  noIndex?: boolean;
}

export function SEO({
  metadata: customMetadata,
  page = 'home',
  params,
  product,
  breadcrumbs,
  faqs,
  title,
  description,
  path,
  schema,
  noIndex,
}: SEOProps) {
  const { faviconUrl, appleTouchIconUrl, themeColor } = useSiteBrand();

  // Get page metadata — explicit title/description (the API every real call
  // site uses) wins over the generic page-keyed defaults.
  const baseMetadata = customMetadata || seoService.generatePageSEO(page, params);
  const metadata: SEOMetadata = title
    ? {
        ...baseMetadata,
        title,
        description: description || baseMetadata.description,
        canonical: path ? `${SITE_URL}${path}` : baseMetadata.canonical,
      }
    : baseMetadata;
  if (noIndex) metadata.noIndex = true;

  // Validate metadata
  const validation = seoService.validateMetadata(metadata);
  if (process.env.NODE_ENV === 'development' && !validation.valid) {
    console.warn('SEO Validation Errors:', validation.errors);
  }

  // Generate structured data — an explicit `schema` prop is used as-is
  // (that's what every real caller passes); otherwise fall back to the
  // auto-generated org/local-business/website graph.
  const structuredData: Record<string, any>[] = schema
    ? (Array.isArray(schema) ? schema : [schema])
    : [
        seoService.generateOrganizationSchema(),
        seoService.generateLocalBusinessSchema(),
        seoService.generateWebSiteSchema(),
      ];

  if (product) {
    structuredData.push(seoService.generateProductSchema(product));
  }

  if (breadcrumbs && breadcrumbs.length > 0) {
    structuredData.push(seoService.generateBreadcrumbSchema(breadcrumbs));
  }

  if (faqs && faqs.length > 0) {
    structuredData.push(seoService.generateFAQSchema(faqs));
  }

  // Track page view
  useEffect(() => {
    analyticsService.pageView(window.location.pathname, metadata.title);
    analyticsService.sendAttribution();
  }, [metadata.title]);

  // index.html ships static <title>/<meta description>/<meta og:*>/<meta
  // twitter:*>/geo/theme-color tags as a no-JS fallback (social-preview
  // scrapers mostly don't execute JS). React 19's native <title>/<meta>
  // hoisting only dedupes among tags IT renders — it won't remove or
  // update those pre-existing static ones, so declaratively rendering the
  // same tag names here would just add a second, duplicate tag with the
  // static one still winning (browsers/crawlers use the first match).
  // Updating the existing DOM nodes in place avoids that duplication.
  useEffect(() => {
    const upsertMeta = (attr: 'name' | 'property', key: string, content: string) => {
      let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    document.title = metadata.title;
    upsertMeta('name', 'title', metadata.title);
    upsertMeta('name', 'description', metadata.description);
    if (metadata.keywords && metadata.keywords.length > 0) {
      upsertMeta('name', 'keywords', metadata.keywords.join(', '));
    }
    upsertMeta('name', 'robots', metadata.noIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    upsertMeta('name', 'author', 'Marple & Co');

    upsertMeta('property', 'og:type', metadata.ogType || 'website');
    if (metadata.canonical) upsertMeta('property', 'og:url', metadata.canonical);
    upsertMeta('property', 'og:title', metadata.title);
    upsertMeta('property', 'og:description', metadata.description);
    upsertMeta('property', 'og:site_name', 'Marple & Co');
    upsertMeta('property', 'og:locale', 'en_ZA');
    if (metadata.ogImage) {
      upsertMeta('property', 'og:image', metadata.ogImage);
      upsertMeta('property', 'og:image:width', '1200');
      upsertMeta('property', 'og:image:height', '630');
      upsertMeta('property', 'og:image:alt', metadata.title);
    }

    upsertMeta('name', 'twitter:card', metadata.twitterCard || 'summary_large_image');
    upsertMeta('name', 'twitter:site', '@marpleandco');
    upsertMeta('name', 'twitter:title', metadata.title);
    upsertMeta('name', 'twitter:description', metadata.description);
    if (metadata.ogImage) upsertMeta('name', 'twitter:image', metadata.ogImage);

    upsertMeta('name', 'theme-color', themeColor || '#1A2E12');

    let canonicalEl = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (metadata.canonical) {
      if (!canonicalEl) {
        canonicalEl = document.createElement('link');
        canonicalEl.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.setAttribute('href', metadata.canonical);
    }
  }, [metadata.title, metadata.description, metadata.canonical, metadata.ogImage, metadata.ogType, metadata.twitterCard, metadata.keywords, metadata.noIndex, themeColor]);

  return (
    <>
      {/* Pinterest */}
      <meta name="pinterest-rich-pin" content="true" />

      {/* Dynamic favicons (admin-configurable branding) */}
      {faviconUrl && (
        <>
          <link rel="icon" type="image/x-icon" href={faviconUrl} />
          <link rel="shortcut icon" href={faviconUrl} />
        </>
      )}
      {appleTouchIconUrl && <link rel="apple-touch-icon" href={appleTouchIconUrl} />}

      {/* Mobile */}
      <meta name="msapplication-TileColor" content={themeColor || '#1A2E12'} />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="Marple & Co" />

      {/* Verification Tags */}
      <meta name="google-site-verification" content="YOUR_GOOGLE_VERIFICATION_CODE" />
      <meta name="facebook-domain-verification" content="YOUR_FB_VERIFICATION_CODE" />
      <meta name="p:domain_verify" content="YOUR_PINTEREST_CODE" />
      
      {/* Structured Data */}
      {structuredData.map((data, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
      
      {/* Preconnect to external domains */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://www.google-analytics.com" />
      <link rel="preconnect" href="https://connect.facebook.net" />
      
      {/* Prefetch important pages */}
      <link rel="prefetch" href="/shop" />
      <link rel="prefetch" href="/skin-analysis" />
    </>
  );
}

// Pre-configured SEO components for common pages
export function HomeSEO() {
  return (
    <SEO 
      page="home" 
      breadcrumbs={[{ name: 'Home', url: '/' }]}
      faqs={[
        {
          question: 'What is Marple Skin Care?',
          answer: 'Marple is South Africa\'s leading AI-powered skincare brand. We combine advanced technology with premium ingredients to create personalized skincare solutions for African skin.'
        },
        {
          question: 'How does the AI skin analysis work?',
          answer: 'Upload photos of your face, answer a few questions, and our AI analyzes your skin type, concerns, and recommends the perfect products for your unique needs.'
        },
        {
          question: 'Do you offer free shipping?',
          answer: 'Yes! We offer free shipping on all orders over R500 within South Africa.'
        },
        {
          question: 'Are Marple products cruelty-free?',
          answer: 'Absolutely! All Marple products are 100% cruelty-free and never tested on animals.'
        },
      ]}
    />
  );
}

export function ShopSEO({ category }: { category?: string }) {
  return (
    <SEO 
      page="shop"
      params={{ category }}
      breadcrumbs={[
        { name: 'Home', url: '/' },
        { name: 'Shop', url: '/shop' },
        ...(category ? [{ name: category, url: `/shop?category=${category}` }] : []),
      ]}
    />
  );
}

export function ProductSEO({ product }: { product: ProductSEOFields['product'] }) {
  return (
    <SEO 
      page="product"
      params={{ name: product.name, description: product.description, image: product.image }}
      product={product}
      breadcrumbs={[
        { name: 'Home', url: '/' },
        { name: 'Shop', url: '/shop' },
        { name: product.category, url: `/shop?category=${product.category}` },
        { name: product.name, url: `/product/${product.sku}` },
      ]}
      faqs={[
        {
          question: `How do I use ${product.name}?`,
          answer: `Apply ${product.name} to clean, dry skin. Follow the instructions on the packaging for best results.`
        },
        {
          question: `Is ${product.name} suitable for my skin type?`,
          answer: `${product.name} is suitable for ${product.category.toLowerCase()}. For personalized recommendations, take our skin quiz!`
        },
        {
          question: 'What are the key ingredients?',
          answer: product.description,
        },
      ]}
    />
  );
}

export function SkinAnalysisSEO() {
  return (
    <SEO 
      page="skin-analysis"
      breadcrumbs={[
        { name: 'Home', url: '/' },
        { name: 'Skin Analysis', url: '/skin-analysis' },
      ]}
      faqs={[
        {
          question: 'Is the skin analysis really free?',
          answer: 'Yes! Our AI skin analysis is completely free. Upload your photos and get personalized recommendations in minutes.'
        },
        {
          question: 'How accurate is the AI analysis?',
          answer: 'Our AI is trained on thousands of skin images and provides highly accurate analysis. However, for serious skin conditions, we recommend consulting a dermatologist.'
        },
        {
          question: 'What photos do I need to upload?',
          answer: 'We need three photos: front-facing, left profile, and right profile. Make sure you\'re in good lighting with no makeup.'
        },
      ]}
    />
  );
}

export function BlogPostSEO({ 
  title, 
  excerpt, 
  image, 
  publishedAt, 
  category,
  slug 
}: { 
  title: string; 
  excerpt: string; 
  image: string; 
  publishedAt: string;
  category: string;
  slug: string;
}) {
  const metadata = seoService.generatePageSEO('blog', { title, description: excerpt, image });
  const articleSchema = seoService.generateArticleSchema({
    title,
    description: excerpt,
    image,
    publishedAt,
    author: 'Marple Skin Care',
    category,
  });

  return (
    <>
      <SEO metadata={metadata} />
      <>
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      </>
    </>
  );
}

export default SEO;
